import { useI18N, useResponsive } from "@sundae/react-hooks";
import {
  Balance,
  Button,
  ButtonGroup,
  DateI18N,
  FadeInBasic,
  Loader,
  Separator,
  Text,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import cx from "classnames";
import isAfter from "date-fns/isAfter";
import { FC, useCallback, useEffect, useMemo } from "react";

import { useTasteTestHistory } from "../../../gql/hooks/taste-test.query";
import { useCurrentTasteTestContribution } from "../../../hooks/taste-test/useCurrentTasteTestContribution";
import { useTasteTestPendingOrderView } from "../../../hooks/taste-test/useTasteTestPendingOrderView";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../../stores";
import { EOrderFlowState, OrderActions, useOrderContext } from "../../../stores/OrderContext";
import NoDataIndicator from "../../NoDataIndicator";

const formatOpts: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
};

interface ITasteTestPendingOrderViewProps {
  isSticky?: boolean;
}

const TasteTestPendingOrderView: FC<ITasteTestPendingOrderViewProps> = ({ isSticky }) => {
  const { currentLocale, t } = useI18N("taste-test");
  const responsive = useResponsive();
  const { details: walletDetails } = useWalletDetails();

  const {
    dispatch,
    state: {
      flowData: { flowState },
    },
  } = useOrderContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const {
    handlers: { removePersistedContributionsHistory },
    state: { persistedContributionsHistory, tasteTest },
  } = useTasteTestContext();
  const { data: tasteTestHistory } = useTasteTestHistory(
    walletDetails.stakingAddress,
    tasteTest?.policyId
  );
  const { buttonGroupConfig, refreshedTimestamp, transactionItems } =
    useTasteTestPendingOrderView();

  const currentContribution = useCurrentTasteTestContribution();
  const currentPendingContribution = persistedContributionsHistory?.[0];

  const isPendingOrderExpired = useMemo(() => {
    if (!currentPendingContribution?.createdAt || !currentPendingContribution?.expiresAt)
      return false;
    const expiresAt = currentPendingContribution.expiresAt;
    const now = new Date().getTime();
    return isAfter(now, expiresAt);
  }, [currentPendingContribution?.createdAt, currentPendingContribution?.expiresAt]);

  const handleExpiredButtonClick = useCallback(() => {
    removePersistedContributionsHistory?.();
    OrderActions.resetOrderFlow(dispatch, "TasteTestOrderView.pendingOrderExpired.onReset");
  }, [dispatch, removePersistedContributionsHistory]);

  const handlePendingOrder = useCallback(() => {
    const isInHistory = tasteTestHistory?.some(
      ({ txHash }) => txHash === currentPendingContribution?.txHash
    );

    if (isInHistory) {
      OrderActions.setOrderFlowState(
        EOrderFlowState.success,
        dispatch,
        "useTasteTest.builtTx.onSuccess"
      );
    }
  }, [
    dispatch,
    flowState,
    removePersistedContributionsHistory,
    tasteTestHistory,
    currentPendingContribution?.txHash,
  ]);

  /**
   *  Memoized node to render - this is to avoid re-rendering of the whole component on every state change;
   */
  const renderMemoizedNode = useCallback(() => {
    if (isPendingOrderExpired) {
      return (
        <FadeInBasic className="flex flex-col items-center justify-center">
          <NoDataIndicator context="tasteTestExpired" />
          <Button className="w-fit" onClick={handleExpiredButtonClick} size="sm" variant="neutral">
            {t("close", { ns: "common" })}
          </Button>
        </FadeInBasic>
      );
    }

    return (
      <>
        <div className="relative z-10 flex flex-col gap-2">
          <div className="inline text-center">
            <Text tag="span" weight="medium" size="lg">
              {t("detail.widget.pendingOrder.title")}
              <Loader className="left-5 inline-flex" size="xsmall" />
            </Text>
          </div>

          <Text className="text-center" tag="span" size="sm" variant="muted">
            {t("detail.widget.pendingOrder.desc")}
          </Text>
        </div>

        <div className="relative z-10">
          <div
            className={cx(
              "absolute -top-[6px] left-2 z-10 h-3 rounded-lg",
              toolkitCx.layout.background.default,
              toolkitCx.layout.spacing.horizontal.sm
            )}
          >
            <Text tag="h3" size="2xs" variant="silent">
              {t("detail.widget.pendingOrder.order", { context: "summary" })}
            </Text>
          </div>

          <div
            className={cx(
              "relative flex flex-col gap-4 rounded-lg",
              toolkitCx.layout.border.default,
              toolkitCx.layout.border.weight.default,
              toolkitCx.layout.background.card,
              toolkitCx.layout.spacing.horizontal.md,
              toolkitCx.layout.spacing.top.lg,
              toolkitCx.layout.spacing.bottom.md
            )}
          >
            <div
              className={cx(
                "relative overflow-hidden",
                "flex flex-col justify-between gap-2 lg:flex-row lg:items-center"
              )}
            >
              {transactionItems.map(({ id, label, value }, index) => (
                <div className="flex h-fit flex-col" key={`${id}-${index}`}>
                  {value}
                  <Text tag="span" size="xs" variant="muted">
                    {label}
                  </Text>
                </div>
              ))}
            </div>

            <Separator
              className={cx("w-full", toolkitCx.layout.background.separator)}
              orientation="horizontal"
            />

            <div className="flex flex-wrap items-start justify-evenly gap-2">
              {currentPendingContribution?.type === "update" && (
                <div className="flex items-center justify-center gap-1">
                  <Text tag="span" size="xs" variant="muted">
                    {t("detail.widget.currentContribution")}:
                  </Text>
                  <Balance
                    abbreviate={false}
                    locale={currentLocale}
                    privacy={privacyEnabled}
                    size="xs"
                    variant="muted"
                    value={currentContribution}
                  />
                </div>
              )}
              <div className="flex items-center justify-center gap-1">
                <Text tag="span" size="xs" variant="muted">
                  {t("detail.widget.pendingOrder.order", { context: "placed" })}:
                </Text>
                <DateI18N
                  formatOpts={formatOpts}
                  locale={currentLocale}
                  size="xs"
                  variant="muted"
                  value={new Date(currentPendingContribution?.createdAt ?? "")}
                />
              </div>

              {refreshedTimestamp && (
                <div
                  className={cx(
                    "flex items-center justify-center gap-1 motion-safe:animate-fadeIn",
                    // rounded-lg for a better ux of the `animate-shadow-pulse` animation
                    "rounded-lg",
                    {
                      "motion-safe:animate-shadow-pulse":
                        buttonGroupConfig.secondaryButtonConfig?.buttonProps?.disabled,
                    }
                  )}
                >
                  <Text tag="span" size="xs" variant="muted">
                    {t("detail.widget.pendingOrder.order", { context: "refreshed" })}:
                  </Text>

                  <DateI18N
                    formatOpts={formatOpts}
                    locale={currentLocale}
                    size="xs"
                    variant="muted"
                    value={refreshedTimestamp}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }, [
    buttonGroupConfig,
    currentContribution,
    currentLocale,
    currentPendingContribution,
    isPendingOrderExpired,
    privacyEnabled,
    refreshedTimestamp,
    t,
    transactionItems,
  ]);

  /**
   * Effect to check if transaction is still pending
   */
  useEffect(() => {
    handlePendingOrder();
  }, [currentPendingContribution, handlePendingOrder, tasteTestHistory]);

  return (
    <FadeInBasic className="flex w-full flex-col">
      <div
        className={cx("relative flex h-fit flex-col gap-2", {
          "w-full self-start": !responsive.lg,
        })}
      >
        <div
          className={cx("rounded-lg", {
            "motion-safe:animate-shadow-pulse": isSticky,
          })}
        >
          <div
            className={cx(
              "flex flex-col gap-8 overflow-hidden rounded-lg",
              toolkitCx.layout.background.default,
              toolkitCx.layout.spacing.both.md,
              {
                "motion-safe:animate-pulse": !isPendingOrderExpired,
              }
            )}
          >
            {renderMemoizedNode()}
          </div>
        </div>

        <FadeInBasic
          className="flex w-full items-center justify-between gap-4"
          transitionDelay={150}
        >
          <ButtonGroup {...buttonGroupConfig} />
        </FadeInBasic>
      </div>
    </FadeInBasic>
  );
};

export default TasteTestPendingOrderView;
